import { makeAutoObservable } from 'mobx';
import { getQrType } from '../data/qrRegistry';
import { DESIGN_PRESETS } from '../data/designPresets';

const STORAGE_KEY = 'vorakit_qrcode';
const RECENT_KEY = 'vorakit_qrcode_recent';
const MAX_RECENT = 10;

const defaultDesign = {
  fgColor: '#000000',
  bgColor: '#ffffff',
  transparentBg: false,
  dotStyle: 'square',
  cornerStyle: 'square',
  margin: 10,
  errorCorrection: 'M',
  logoUrl: null,
  logoSize: 0.25,
  logoPadding: 4,
};

const getInitialState = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return { ...defaultDesign, ...parsed };
    }
  } catch (_) {}
  return { ...defaultDesign };
};

class QrCodeStore {
  qrTypeId = 'url';
  formData = {};
  design = getInitialState();
  exportSize = 512;
  customExportSize = 1024;
  recentConfigs = [];
  validationErrors = {};
  lastPayload = '';
  generatedPayload = '';

  constructor() {
    makeAutoObservable(this);
    this.loadRecent();
  }

  get qrType() {
    return getQrType(this.qrTypeId);
  }

  get payload() {
    const type = this.qrType;
    return type.buildPayload(this.formData);
  }

  generate() {
    this.generatedPayload = this.payload;
    if (this.generatedPayload) {
      this.addToRecent(this.generatedPayload, this.qrTypeId);
    }
  }

  clearGenerated() {
    this.generatedPayload = '';
  }

  setQrType(id) {
    this.qrTypeId = id;
    const type = getQrType(id);
    const initial = {};
    type.fields.forEach((f) => {
      initial[f.id] = this.formData[f.id] ?? f.default ?? '';
    });
    this.formData = initial;
    this.validationErrors = {};
    this.generatedPayload = '';
  }

  setFormData(id, value) {
    this.formData = { ...this.formData, [id]: value };
    if (this.validationErrors[id]) {
      const { [id]: _, ...rest } = this.validationErrors;
      this.validationErrors = rest;
    }
  }

  setDesign(updates) {
    this.design = { ...this.design, ...updates };
    this.persistDesign();
  }

  resetDesign() {
    this.design = { ...defaultDesign };
    this.persistDesign();
  }

  setExportSize(size) {
    this.exportSize = size;
  }

  setCustomExportSize(size) {
    const n = parseInt(size, 10);
    this.customExportSize = isNaN(n) ? 512 : Math.max(1, Math.min(4096, n));
  }

  applyPreset(presetId) {
    const preset = DESIGN_PRESETS[presetId];
    if (!preset) return;
    const c = preset.config;
    this.design = {
      ...this.design,
      fgColor: c.dotsOptions?.color ?? this.design.fgColor,
      bgColor: c.backgroundOptions?.color ?? this.design.bgColor,
      transparentBg: false,
      dotStyle: c.dotsOptions?.type ?? this.design.dotStyle,
      cornerStyle: c.cornersSquareOptions?.type ?? this.design.cornerStyle,
      margin: c.margin ?? this.design.margin,
      errorCorrection: c.errorCorrectionLevel ?? this.design.errorCorrection,
    };
    this.persistDesign();
  }

  setValidationError(id, error) {
    if (error) this.validationErrors = { ...this.validationErrors, [id]: error };
    else {
      const { [id]: _, ...rest } = this.validationErrors;
      this.validationErrors = rest;
    }
  }

  persistDesign() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.design));
    } catch (_) {}
  }

  addToRecent(payload, typeId) {
    const entry = { payload: payload?.slice(0, 100), typeId, ts: Date.now() };
    this.recentConfigs = [
      entry,
      ...this.recentConfigs.filter((r) => r.payload !== entry.payload).slice(0, MAX_RECENT - 1),
    ];
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(this.recentConfigs));
    } catch (_) {}
  }

  loadRecent() {
    try {
      const saved = localStorage.getItem(RECENT_KEY);
      if (saved) this.recentConfigs = JSON.parse(saved);
    } catch (_) {}
  }

  getQrStylingOptions() {
    const d = this.design;
    return {
      width: 256,
      height: 256,
      data: this.payload || 'https://vorakit.app',
      margin: d.margin,
      qrOptions: { type: d.dotStyle, color: d.fgColor },
      imageOptions: {
        hideBackgroundDots: true,
        imageSize: d.logoSize,
        margin: d.logoPadding,
      },
      dotsOptions: { type: d.dotStyle, color: d.fgColor },
      cornersSquareOptions: { type: d.cornerStyle, color: d.fgColor },
      cornersDotOptions: { type: d.dotStyle, color: d.fgColor },
      backgroundOptions: {
        color: d.transparentBg ? 'transparent' : d.bgColor,
        round: 0,
      },
      image: d.logoUrl || undefined,
      errorCorrectionLevel: d.errorCorrection,
    };
  }
}

export const qrCodeStore = new QrCodeStore();
